import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CreditCardOff as ExpiredIcon,
  Payment as PaymentIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';

const SubscriptionExpired = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [resubLoading, setResubLoading] = useState(false);
  const [error, setError] = useState('');

  const handleManageBilling = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await userAPI.createBillingPortalSession();
      window.location.href = res.data.url;
    } catch (err) {
      // If billing portal fails (no existing Stripe customer/subscription),
      // suggest resubscribe instead
      setError('Unable to open billing portal. Use "Resubscribe" below to start a new subscription.');
      setLoading(false);
    }
  };

  const handleResubscribe = async () => {
    setResubLoading(true);
    setError('');
    try {
      const res = await userAPI.createResubscribeSession();
      window.location.href = res.data.url;
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create checkout session. Please contact support.';
      setError(msg);
      setResubLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleRetry = () => {
    // Clear the flag and try navigating back to dashboard
    sessionStorage.removeItem('subscriptionExpired');
    navigate('/dashboard');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <ExpiredIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />

          <Typography variant="h5" gutterBottom fontWeight={600}>
            Subscription Inactive
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your Second Clear subscription is no longer active.
            Please update your payment method to continue protecting
            {user?.name ? ` ${user.name}'s` : ' your'} account.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PaymentIcon />}
              onClick={handleManageBilling}
              disabled={loading || resubLoading}
            >
              {loading ? 'Opening Billing...' : 'Update Payment Method'}
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={handleResubscribe}
              disabled={loading || resubLoading}
            >
              {resubLoading ? 'Redirecting...' : 'Resubscribe'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleRetry}
            >
              I already updated - try again
            </Button>

            <Button
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{ color: 'text.secondary' }}
            >
              Log Out
            </Button>
          </Box>

          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 3 }}>
            Need help? Contact support@secondclear.com
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
            Or{' '}
            <Typography
              component="span"
              variant="caption"
              sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate('/feedback')}
            >
              submit a request from your dashboard
            </Typography>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default SubscriptionExpired;
